import { useComputeReturnFees } from "../queries/use-compute-return-fees";
import { usePublicOrder } from "../queries/use-public-order";
import { feeToCents, type FeeResolutionKey } from "../lib/return-fee.lib";

export const PRICE_PLACEHOLDER = "…";

/** `current` prices the basket as it stands; the others hypothesise a single resolution. */
type FeeKey = FeeResolutionKey | "current";

export type HypotheticalReturnFeeState =
  /** Query pending — nothing to display yet. */
  | { status: "loading"; fee: undefined; fallbackFee: number; currency: string | null }
  /** Shop opted out (e.g. discount with `applyReturnShippingFeesForDiscountRule` disabled). Fee is hard zero. */
  | { status: "opt-out"; fee: 0; fallbackFee: number; currency: string }
  /** Dynamic fee returned by the backend. `isStale` true while refetching. */
  | { status: "ready"; fee: number; fallbackFee: number; currency: string | null; isStale: boolean };

/**
 * Resolves a return fee from the `compute-return-fees` query, **in cents**, with three
 * distinct states:
 *
 * - `loading`: no data yet — show PRICE_PLACEHOLDER, disable card.
 * - `opt-out`: backend returned `null` for this resolution — fee is hard 0.
 * - `ready`: `fee` is the dynamic amount. `isStale` true while refetching.
 *
 * `fallbackFee` exposes the legacy static amount (from order.financialInformations)
 * so callers can avoid flicker on first mount by opting in explicitly.
 */
export function useHypotheticalReturnFee(
  resolution: FeeKey,
  {
    orderId,
    shopId,
    pendingLineItemId,
    pendingReasonId,
  }: {
    orderId: string;
    shopId: string;
    pendingLineItemId?: string;
    pendingReasonId?: string;
  },
): HypotheticalReturnFeeState {
  const { data: order } = usePublicOrder(orderId);
  const { data: returnFees, isFetching, isPlaceholderData, isError } =
    useComputeReturnFees({ orderId, shopId, pendingLineItemId, pendingReasonId });

  const fallbackFee = feeToCents(
    order?.financialInformations?.return_shipping_paid?.presentment_amount ?? 0,
  );

  // Query failed with no prior data — fall back to the static order fee.
  if (isError && !returnFees) {
    return { status: "ready", fee: fallbackFee, fallbackFee, currency: null, isStale: false };
  }

  if (!returnFees) {
    return { status: "loading", fee: undefined, fallbackFee, currency: null };
  }

  const dynamicFee =
    resolution === "current"
      ? returnFees.current
      : returnFees.byResolution[resolution];
  if (dynamicFee === null) {
    return { status: "opt-out", fee: 0, fallbackFee, currency: returnFees.currency };
  }

  return {
    status: "ready",
    fee: feeToCents(dynamicFee),
    fallbackFee,
    currency: returnFees.currency,
    isStale: isFetching && isPlaceholderData,
  };
}
